// ============================================================================
// FILE: pages/Campaigns/Lead/CreateEditLeadModal.js (UPDATED - Single Source Only)
// ============================================================================

import React, { useState, useEffect } from "react";
import { Modal as MuiModal, Box, Button as MuiButton, TextField, MenuItem } from '@mui/material';
import axiosInstance from "../../../utils/axiosInstance";

export default function CreateEditLeadModal({ isOpen, onClose, onSuccess, lead, mode, chatbotId, activeSource, permission }) {
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        company: '',
        email: '',
        phone: '',
        status: 'New',
        sales_stage: 'New',
        title: '',
        street: '',
        city: '',
        state: '',
        postal_code: '',
        country: '',
        website: '',
        description: ''
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const statusOptions = [
        'New',
        'Qualified',
        'Unqualified',
        'Working - Contacted',
        'Closed - Converted',
        'Closed - Not Converted',
        'Open - Not Contacted'
    ];

    const salesStageOptions = [
        'First Meeting - Demo',
        'Solution & POC',
        'Negotiation & Legal'
    ];

    useEffect(() => {
        if (lead && mode === 'edit') {
            setFormData({
                first_name: lead.first_name || '',
                last_name: lead.last_name || '',
                company: lead.company || '',
                email: lead.email || '',
                phone: lead.phone || '',
                status: lead.status || 'New',
                title: lead.title || '',
                street: lead.street || '',
                city: lead.city || '',
                state: lead.state || '',
                postal_code: lead.postal_code || '',
                country: lead.country || '',
                website: lead.website || '',
                description: lead.description || ''
            });
        } else {
            // Reset form for create mode
            setFormData({
                first_name: '',
                last_name: '',
                company: '',
                email: '',
                phone: '',
                status: 'New',
                title: '',
                street: '',
                city: '',
                state: '',
                postal_code: '',
                country: '',
                website: '',
                description: ''
            });
        }
        setErrors({});
        setSubmitError('');
    }, [lead, mode, isOpen]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.last_name.trim()) {
            newErrors.last_name = 'Last Name is required';
        }
        
        // Salesforce requires company
        if (activeSource === 'salesforce' && !formData.company.trim()) {
            newErrors.company = 'Company is required for Salesforce leads';
        }

        if (activeSource === 'customer_portal' && !formData.first_name.trim()) {
            newErrors.first_name = 'First name is required for Customer Portal leads';
        }

         if (activeSource === 'customer_portal' && !formData.email.trim()) {
            newErrors.email = 'Email is required for Customer Portal leads';
        }
        
        
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Invalid email format';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        setSubmitError('');

        try {
            // Remove empty fields
            const cleanedData = Object.entries(formData).reduce((acc, [key, value]) => {
                if (value && value.toString().trim() !== '') {
                    acc[key] = value;
                }
                return acc;
            }, {});

            const params = chatbotId ? { chatbot_id: chatbotId } : {};

            if (mode === 'create') {
                await axiosInstance.post('/api/leads', cleanedData, { params });
            } else {
                // For edit, include source in params
                await axiosInstance.put(
                    `/api/leads/${lead.source_id || lead.id}`, 
                    cleanedData, 
                    { 
                        params: {
                            ...params,
                            source: lead.source_type
                        }
                    }
                );
            }
            
            onSuccess();
        } catch (err) {
            console.error(`Error ${mode === 'create' ? 'creating' : 'updating'} lead:`, err);
            setSubmitError(err.response?.data?.message || `Failed to ${mode === 'create' ? 'create' : 'update'} lead`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '95%', sm: '90%', md: '70%' },
        maxWidth: '800px',
        maxHeight: '90vh',
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 3,
        borderRadius: '8px',
        outline: 'none',
        overflowY: 'auto'
    };

    const getSourceBadge = (sourceType) => {
        if (sourceType === 'salesforce') {
            return <span className="badge bg-primary"><i className="fa fa-cloud me-1"></i>Salesforce</span>;
        } else {
            return <span className="badge bg-success"><i className="fa fa-user-circle me-1"></i>Customer Portal</span>;
        }
    };

    return (
        <MuiModal open={isOpen} onClose={onClose} aria-labelledby="lead-modal-title">
            <Box sx={modalStyle}>
                <h5 id="lead-modal-title" style={{ marginTop: 0, marginBottom: '20px' }}>
                    {mode === 'create' ? 'Create New Lead' : 'Edit Lead'}
                </h5>

                {submitError && (
                    <div className="alert alert-danger mb-3" role="alert">
                        {submitError}
                    </div>
                )}

                {/* Source Display */}
                <div className="mb-3">
                    <strong>Destination: </strong>
                    {mode === 'edit' && lead ? (
                        getSourceBadge(lead.source_type)
                    ) : (
                        getSourceBadge(activeSource)
                    )}
                    {activeSource === 'salesforce' && (
                        <small className="text-muted ms-2">
                            (Company field is required for Salesforce)
                        </small>
                    )}
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <TextField
                                fullWidth
                                label="First Name"
                                name="first_name"
                                value={formData.first_name}
                                onChange={handleChange}
                                error={!!errors.first_name}
                                helperText={errors.first_name || (activeSource === 'customer_portal' ? 'Required for Customer Portal' : '')}
                                variant="outlined"
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <TextField
                                fullWidth
                                required
                                label="Last Name"
                                name="last_name"
                                value={formData.last_name}
                                onChange={handleChange}
                                error={!!errors.last_name}
                                helperText={errors.last_name}
                                variant="outlined"
                            />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <TextField
                                fullWidth
                                required={activeSource === 'salesforce'}
                                label="Company"
                                name="company"
                                value={formData.company}
                                onChange={handleChange}
                                error={!!errors.company}
                                helperText={errors.company || (activeSource === 'salesforce' ? 'Required for Salesforce' : '')}
                                variant="outlined"
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <TextField
                                fullWidth
                                label="Title"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                variant="outlined"
                            />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <TextField
                                fullWidth
                                label="Email"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleChange}
                                error={!!errors.email}
                                helperText={errors.email || (activeSource === 'customer_portal' ? 'Required for Customer Portal' : '')}
                                variant="outlined"
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <TextField
                                fullWidth
                                label="Phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                variant="outlined"
                            />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <TextField
                                fullWidth
                                select
                                label="Status"
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                                variant="outlined"
                            >
                                {statusOptions.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </div>
                        
                        <div className="col-md-6 mb-3">
                            <TextField
                                fullWidth
                                label="Website"
                                name="website"
                                value={formData.website}
                                onChange={handleChange}
                                variant="outlined"
                            />
                        </div>
                    </div>
                    {formData.status == 'Working - Contacted' && (
                        <div className="row">
                            <div className="col-md-6 mb-3">
                                <TextField
                                    fullWidth
                                    select
                                    label="Sales Stage"
                                    name="sales_stage"
                                    value={formData.sales_stage}
                                    onChange={handleChange}
                                    variant="outlined"
                                >
                                    {salesStageOptions.map((option) => (
                                        <MenuItem key={option} value={option}>
                                            {option}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </div>
                        </div>
                    )}
                    

                    <h6 className="mt-3 mb-2">Address Information</h6>
                    <div className="row">
                        <div className="col-12 mb-3">
                            <TextField
                                fullWidth
                                label="Street"
                                name="street"
                                value={formData.street}
                                onChange={handleChange}
                                variant="outlined"
                            />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <TextField
                                fullWidth
                                label="City"
                                name="city"
                                value={formData.city}
                                onChange={handleChange}
                                variant="outlined"
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <TextField
                                fullWidth
                                label="State"
                                name="state"
                                value={formData.state}
                                onChange={handleChange}
                                variant="outlined"
                            />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <TextField
                                fullWidth
                                label="Postal Code"
                                name="postal_code"
                                value={formData.postal_code}
                                onChange={handleChange}
                                variant="outlined"
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <TextField
                                fullWidth
                                label="Country"
                                name="country"
                                value={formData.country}
                                onChange={handleChange}
                                variant="outlined"
                            />
                        </div>
                    </div>

                    <div className="mb-3">
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label="Description"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            variant="outlined"
                        />
                    </div>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                        <MuiButton variant="outlined" onClick={onClose} disabled={isSubmitting}>
                            Cancel
                        </MuiButton>
                        {permission.canModify && (
                            <MuiButton 
                                type="submit"
                                variant="contained" 
                                disabled={isSubmitting}
                                startIcon={isSubmitting && <i className="fa fa-spinner fa-spin"/>}
                            >
                                {isSubmitting ? (mode === 'create' ? 'Creating...' : 'Updating...') : (mode === 'create' ? 'Create Lead' : 'Update Lead')}
                            </MuiButton>
                        )}
                    </Box>
                </form>
            </Box>
        </MuiModal>
    );
}